import axios from 'axios';
const API_BASE_URL = 'https://rag-api-289455571528.us-central1.run.app';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});


const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add the missing handleResponse function
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }
  return {
    data: await response.json(),
    status: response.status
  };
};

// Update the test chat method
const testChat = (businessId, data) => {
  // Ensure conversation_id is null or marked as test
  const testData = {
    ...data,
    conversation_id: null,  // Force null to ensure it's not saved 
    is_test: true,  // Add explicit test flag
    // Keep chat_history if it exists in the data
  };
  return apiClient.post(`/test-chat/${businessId}`, testData);
};

// Add Google Places API methods
const googlePlaces = {
  search: async (query, location = null) => {
    const response = await fetch(`${API_BASE_URL}/google-places/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        textQuery: query,  // Changed from 'query' to 'textQuery' to match backend
        pageSize: 10,
        locationBias: location ? {
          circle: {
            center: { latitude: location.lat, longitude: location.lng },
            radius: 50000
          }
        } : null
      })
    });
    return handleResponse(response);
  },

  getDetails: async (placeId) => {
    const response = await fetch(`${API_BASE_URL}/google-places/details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        place_id: placeId
      })
    });
    return handleResponse(response);
  },

  autocomplete: async (input, types = 'establishment') => {
    const params = new URLSearchParams({
      input,
      types,
      radius: '50000'
    });
    
    const response = await fetch(`${API_BASE_URL}/google-places/autocomplete?${params}`);
    return handleResponse(response);
  }
};

export const api = {
  // Business endpoints
  createBusiness: (data) => 
    apiClient.post('/businesses', data)
      .then(response => {
        console.log('Raw API Response:', response.data);
        // Map Firebase id to business_id
        return {
          data: {
            ...response.data,
            business_id: response.data.id || response.data.business_id
          }
        };
      })
      .catch(error => {
        if (error.response?.status === 409) {
          return api.getBusinessByName(data.name)
            .then(response => ({
              data: {
                ...response.data,
                business_id: response.data.id || response.data.business_id
              }
            }));
        }
        throw error;
      }),
  getBusiness: (businessId) => 
    apiClient.get(`/businesses/${businessId}`)
      .then(response => ({
        data: {
          ...response.data,
          settings: response.data.settings || {
            basePrompt: '',
            welcomeMessage: '',
            primaryColor: '#2563EB'
          },
          updated_at: response.data.updated_at
        }
      })),
  getBusinessByName: (name) => apiClient.get(`/businesses/by-name/${name}`),
  getBusinessByEmail: (email) => 
    apiClient.get(`/businesses/by-email/${email}`)
      .catch(error => {
        if (error.response?.status === 404) {
          // Return null data when no business found
          return { data: null };
        }
        throw error;
      }),
  updateBusiness: (businessId, data) => 
    apiClient.put(`/businesses/${businessId}`, {
      settings: {
        ...data.settings,
        updatedAt: new Date().toISOString()
      }
    }),


  deleteBusiness: (businessId) => 
    apiClient.delete(`/businesses/${businessId}`),

  // Document endpoints
  uploadDocument: (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/businesses/${businessId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listDocuments: (businessId) => apiClient.get(`/businesses/${businessId}/documents`),
  deleteDocument: (businessId, documentId) => 
    apiClient.delete(`/businesses/${businessId}/documents/${documentId}`),

  // Add new method for website processing with improved error handling
  processWebsite: (businessId, data) => 
    apiClient.post(`/businesses/${businessId}/documents/website`, data)
      .catch(error => {
        // Extract the specific error message if available
        if (error.response?.data?.detail) {
          console.error(`Website processing error: ${error.response.data.detail}`);
          error.message = error.response.data.detail;
        } else {
          console.error(`Website processing error: ${error.message}`);
        }
        throw error;
      }),

  // Add new method to check processing status
  waitForProcessing: async (businessId, documentId) => {
    const maxAttempts = 30; // Maximum number of attempts
    const delay = 2000; // 2 seconds between attempts

    for (let i = 0; i < maxAttempts; i++) {
      const response = await apiClient.get(`/businesses/${businessId}/documents/${documentId}/status`);
      if (response.data.status === 'indexed') {
        return response.data;
      } else if (response.data.status === 'failed') {
        throw new Error('Processing failed');
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    throw new Error('Processing timeout');
  },

  // Chat endpoints
  sendMessage: (businessId, data) => 
    apiClient.post(`/businesses/${businessId}/chat`, {
      message: data.message,
      business_id: data.business_id,
      conversation_id: data.conversation_id,
      whatsapp_enabled: data.whatsapp_enabled,
      whatsapp_number: data.whatsapp_number
    }),

  clearChatHistory: async (businessId) => {
    const response = await apiClient.post(`/businesses/${businessId}/clear-chat`);
    return response.data;
  },

  // Conversation endpoints
  listConversations: (businessId) => 
    apiClient.get(`/businesses/${businessId}/conversations`)
      .then(response => {
        // Map backend fields for sidebar preview
        if (Array.isArray(response.data.conversations)) {
          response.data.conversations = response.data.conversations.map(conv => ({
            ...conv,
            last_user_message: conv.last_user_message || '',
            last_assistant_message: conv.last_assistant_message || '',
            created_at: conv.created_at,
            updated_at: conv.updated_at,
            id: conv.id
          }));
        }
        return response;
      }),
  getConversation: (businessId, conversationId) => 
    apiClient.get(`/businesses/${businessId}/conversations/${conversationId}`)
      .then(response => {
        // Process messages to ensure timestamps
        if (response.data.messages) {
          response.data.messages = response.data.messages.map(msg => {
            // Fix timestamps if missing
            if (!msg.timestamp) {
              const now = new Date();
              
              // Try to create timestamp from the time field if available
              if (msg.time) {
                // Simple conversion from time field to ISO string
                try {
                  const parts = msg.time.match(/(\d+):(\d+)(?::(\d+))?\s*([AP]M)?/i);
                  if (parts) {
                    const [_, hours, minutes, seconds = 0, ampm] = parts;
                    let hour = parseInt(hours);
                    
                    // Convert to 24-hour format if AM/PM is specified
                    if (ampm && ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
                    if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
                    
                    // Create a new date with just the time parts (use today's date)
                    const date = new Date();
                    date.setHours(hour, parseInt(minutes), seconds ? parseInt(seconds) : 0);
                    
                    msg.timestamp = date.toISOString();
                  } else {
                    // If we can't parse the time format, just use current time
                    msg.timestamp = now.toISOString();
                  }
                } catch (e) {
                  console.warn('Error converting time to timestamp:', e);
                  msg.timestamp = now.toISOString();
                }
              } else {
                // No time field, use current time
                msg.timestamp = now.toISOString();
              }
            }
            
            return msg;
          });
        }
        
        return response;
      }),
  deleteConversation: (businessId, conversationId) => 
    apiClient.delete(`/businesses/${businessId}/conversations/${conversationId}`),
  saveConversation: (businessId, conversationId, data, is_new_message = false) => {
    // Ensure all messages have timestamps before saving
    const updatedData = { ...data };
    
    if (Array.isArray(updatedData.messages)) {
      updatedData.messages = updatedData.messages.map(msg => {
        if (!msg.timestamp) {
          const now = new Date();
          return {
            ...msg,
            timestamp: now.toISOString(),
            time: now.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })
          };
        }
        return msg;
      });
    }
    
    return apiClient.post(`/businesses/${businessId}/conversations/${conversationId}`, {
      ...updatedData,
      is_new_message
    });
  },
  downloadConversation: (businessId, conversationId) => 
    apiClient.get(`/businesses/${businessId}/conversations/${conversationId}`, {
      transformResponse: [(data) => data], // Prevent JSON parsing
      responseType: 'text'
    }),

  createDataSource: async (businessId, data) => {
    try {
      // Check if this is an FAQ submission
      if (data.type === 'faq') {
        // Format FAQ data appropriately for the backend including filename
        const response = await apiClient.post(
          `/businesses/${businessId}/datasources`,
          {
            type: 'faq',
            faqs: data.faqs, // Array of {question, answer} objects
            // We don't need to add filename here as backend will generate it
          }
        );
        return response;
      } else {
        // Extract filename from URL for other data types
        const filename = data.url.split('/').pop() || 'untitled';
        const response = await apiClient.post(
          `/businesses/${businessId}/datasources`,
          {
            ...data,
            filename,  // Add filename field
            documentType: data.type // Ensure documentType is set
          }
        );
        return response;
      }
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Data source creation error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  // Analytics endpoints
  getAnalytics: (businessId, timeRange = '30d') => 
    apiClient.get(`/businesses/${businessId}/analytics`, { 
      params: { timeRange } 
    })
    .catch(error => {
      console.error('Analytics error:', error);
      // If the endpoint is not implemented yet, return mock data
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.log('Returning mock analytics data');
      }
      throw error;
    }),
  testChat,     // Add this new method
  // Add widget settings API methods with debugging
  getWidgetSettings: (businessId) => {
    console.log(`[API] Getting widget settings for business: ${businessId}`);
    return apiClient.get(`/widget/settings/${businessId}`)
      .then(response => {
        console.log('[API] Widget settings response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error getting widget settings:', error);
        throw error;
      });
  },

  updateWidgetSettings: (businessId, settings) => {
    console.log(`[API] Updating widget settings for business: ${businessId}`);
    console.log('[API] Settings payload:', settings);
    
    return apiClient.put(`/widget/settings/${businessId}`, settings)
      .then(response => {
        console.log('[API] Update widget settings response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error updating widget settings:', error);
        console.error('[API] Error response data:', error.response?.data);
        throw error;
      });
  },

  googlePlaces,
  
  // Update createBusiness to handle Google Places data
  createBusiness: async (businessData) => {
    const response = await fetch(`${API_BASE_URL}/businesses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(businessData)
    });
    return handleResponse(response);
  },

  // Add website scraping method
  scrapeWebsite: async (data) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/scrape-website`, data);
      return response;
    } catch (error) {
      console.error('API Error in scrapeWebsite:', error);
      throw error;
    }
  },

  // Add Google Places indexing method with better error handling
  indexGooglePlaces: async (data) => {
    try {
      const response = await apiClient.post('/google-places-index', data);
      return response;
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Google Places indexing error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  // Add processGooglePlaces method for business setup
  processGooglePlaces: async (businessId, data) => {
    try {
      const response = await apiClient.post('/google-places-index', {
        business_id: businessId,
        place_id: data.place_id,
        name: data.name || '',
        formatted_address: data.formatted_address || '',
        formatted_phone_number: data.formatted_phone_number || '',
        international_phone_number: data.international_phone_number || '',
        website: data.website || '',
        types: data.types || [],
        primary_category: data.primary_category || '',
        geometry: data.geometry || {},
        opening_hours: data.opening_hours || {},
        business_status: data.business_status || '',
        rating: data.rating || null,
        user_ratings_total: data.user_ratings_total || null,
        review_summary: data.review_summary || null
      });
      return response;
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Google Places processing error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  confirmWebsiteContent: async (businessId, data) => {
    return await apiClient.post(`/businesses/${businessId}/website-content/confirm`, data);
  },

  getDocumentContent: async (businessId, documentId) => {
    return await api.get(`/businesses/${businessId}/documents/${documentId}/content`);
  },

  // Add reindex method with proper error handling and logging
  reindexDocument: (businessId, documentId, type = 'google_places') => {
    console.log(`Calling reindex API for business: ${businessId}, document: ${documentId}, type: ${type}`);
    let endpoint;
    if (type === 'google_places') {
      endpoint = `/businesses/${businessId}/documents/${documentId}/reindex-google-place`;
    } else if (type === 'url') {
      endpoint = `/businesses/${businessId}/documents/${documentId}/reindex-website`;
    } else {
      throw new Error('Unsupported document type for reindex');
    }
    // Always POST to this endpoint for both index and re-index
    return axios.post(`${API_BASE_URL}${endpoint}`, {}, {
      timeout: 120000 // 2 minutes for reindexing
    })
    .then(response => {
      console.log('Reindex API success response:', response.data);
      return response;
    })
    .catch(error => {
      console.error('Reindex API error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    });
  },

  // Add logo upload method for widget branding
  uploadWidgetLogo: async (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use axios directly for file upload
    return axios.post(
      `${API_BASE_URL}/businesses/${businessId}/widget/upload-logo`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      }
    );
  },
  removeWidgetLogo: async (businessId) => {
    return axios.delete(`${API_BASE_URL}/businesses/${businessId}/widget/remove-logo`);
  },
  uploadWidgetLogoWithCrop: async (businessId, formData) => {
    return axios.post(
      `${API_BASE_URL}/businesses/${businessId}/widget/upload-logo`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      }
    );
  },

  // Add business profile fetch method
  getBusinessProfile: (businessId) =>
    apiClient.get(`/business-profile/${businessId}`),
  
  improveMessage: (businessId, conversationId, messageIndex, improvedAnswer) =>
  apiClient.post(`/businesses/${businessId}/conversations/${conversationId}/improve`, {
    message_index: messageIndex,
    improved_answer: improvedAnswer
  }),
};


